import { Alert, Button, Drawer, Group, Stack, Textarea } from '@mantine/core';
import { IconBooks } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../format.js';
import { StatusBadge } from '../../StatusBadge.js';
import { APPLICATION_TONE } from './Applications.js';
import { useLibraryErrorText } from './LibraryTables.js';
import classes from './PmsLibrary.module.css';
import type { LibraryRequestSource } from './source.js';
import type { ApplicationPlan, LibraryApplication } from './types.js';
import { intervalLabel } from './view.js';

/**
 * Gemide PMS kütüphanesi talepleri (ADR 0063 §7): Equipment List'in üstünde şerit; çekmecede
 * talebin içeriği, güncel ağaca göre önizleme, kabul (gemi yazar) ve gerekçeli ret. Bekleyen talep
 * yoksa hiçbir şey çizilmez.
 */
export function LibraryRequestsBanner({
  source,
  onApplied,
}: {
  source: LibraryRequestSource;
  onApplied: () => void;
}) {
  const { t } = useTranslation();
  const [data, setData] = useState<{
    applications: LibraryApplication[];
    can: { decide: boolean };
  } | null>(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    try {
      setData(await source.list());
    } catch {
      setData(null);
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);
  const pending = data?.applications.filter((a) => a.status === 'pending') ?? [];
  if (!data || pending.length === 0) return null;
  return (
    <>
      <section className={classes.banner} aria-label={t('pmsLibrary.requests.title')}>
        <div className={classes.bannerText}>
          <strong>{t('pmsLibrary.requests.banner', { count: pending.length })}</strong>
          <span>{t('pmsLibrary.requests.bannerText')}</span>
        </div>
        <Button leftSection={<IconBooks size={16} />} onClick={() => setOpen(true)}>
          {t('pmsLibrary.actions.review')}
        </Button>
      </section>
      <Drawer
        opened={open}
        onClose={() => setOpen(false)}
        position="right"
        size="lg"
        title={t('pmsLibrary.requests.title')}
        closeButtonProps={{ 'aria-label': t('pmsLibrary.actions.close') }}
      >
        <Stack gap="md">
          {pending.map((application) => (
            <RequestCard
              key={application.id}
              application={application}
              canDecide={data.can.decide}
              source={source}
              onDecided={async () => {
                await load();
                onApplied();
              }}
            />
          ))}
        </Stack>
      </Drawer>
    </>
  );
}

function RequestCard({
  application,
  canDecide,
  source,
  onDecided,
}: {
  application: LibraryApplication;
  canDecide: boolean;
  source: LibraryRequestSource;
  onDecided: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useLibraryErrorText();
  const [plan, setPlan] = useState<ApplicationPlan | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<unknown>(null);
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pmsLibrary.requests.${key}`, values);
  useEffect(() => {
    source
      .preview(application.id)
      .then(setPlan)
      .catch((failure: unknown) => setPreviewError(failure ?? 'GENERIC'));
  }, [application.id, source]);
  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await onDecided();
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  };
  const { content } = application;
  return (
    <article className={classes.requestCard} aria-label={`${k('title')} ${application.id}`}>
      <div className={classes.requestHead}>
        <span>
          {k('sentBy', {
            name: application.createdByName ?? '—',
            date: formatDateTime(application.createdAt),
          })}
        </span>
        <StatusBadge tone={APPLICATION_TONE[application.status]}>
          {t(`pmsLibrary.applicationStatuses.${application.status}`)}
        </StatusBadge>
      </div>
      {application.note ? <p className={classes.ioText}>{application.note}</p> : null}
      <strong>{k('counts', { items: application.itemCount, jobs: application.jobCount })}</strong>
      <div>
        <span className={classes.muted}>{k('items')}</span>
        <ul className={classes.list}>
          {content.items.map((item) => (
            <li key={item.libraryId}>
              {item.code} {item.name}
              {item.tag ? ` · ${item.tag}` : ''}
              {item.location ? ` · ${item.location}` : ''}
              {item.critical ? ` · ${t('pmsLibrary.columns.critical')}` : ''}
            </li>
          ))}
        </ul>
      </div>
      {content.jobs.length > 0 ? (
        <div>
          <span className={classes.muted}>{k('jobs')}</span>
          <ul className={classes.list}>
            {content.jobs.map((job) => (
              <li key={job.libraryId}>
                {job.code} {job.title} — {intervalLabel(job.interval, t)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {plan && plan.errors.length > 0 ? (
        <Alert color="red">
          {k('invalid')}
          <ul className={classes.list}>
            {plan.errors.map((e) => (
              <li key={`${e.kind}-${e.libraryId}-${e.code}`}>
                {t(`pmsLibrary.applyErrors.${e.code}`, { defaultValue: e.code })}
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}
      {previewError ? <Alert color="red">{errorText(previewError)}</Alert> : null}
      {error ? <Alert color="red">{error}</Alert> : null}
      {!canDecide ? <p className={classes.ioText}>{k('noPermission')}</p> : null}
      {rejecting ? (
        <Textarea
          label={k('reason')}
          description={k('reasonHint')}
          required
          autosize
          minRows={2}
          maxLength={1000}
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
        />
      ) : null}
      {canDecide ? (
        <Group className={classes.requestActions}>
          {rejecting ? (
            <>
              <Button
                color="red"
                loading={busy}
                disabled={reason.trim() === ''}
                onClick={() => void run(() => source.reject(application.id, reason.trim()))}
              >
                {t('pmsLibrary.actions.reject')}
              </Button>
              <Button variant="default" onClick={() => setRejecting(false)}>
                {t('pmsLibrary.actions.cancel')}
              </Button>
            </>
          ) : (
            <>
              <Button
                loading={busy}
                disabled={!plan || plan.errors.length > 0}
                onClick={() => void run(() => source.accept(application.id))}
              >
                {t('pmsLibrary.actions.accept')}
              </Button>
              <Button variant="default" color="red" onClick={() => setRejecting(true)}>
                {t('pmsLibrary.actions.reject')}
              </Button>
            </>
          )}
        </Group>
      ) : null}
    </article>
  );
}
